import { BCMSDate, BCMSDefaultModel, DisabledPrompt, useBCMSContext, BCMSStateManagerCRUD, useStateManager, BCMSRecord, StateManager, useRoute, useWorkflow } from "bcms-react";
import Classes, { Payee } from "model";
import CustomSelect, { CustomSelectItem } from 'components/custom-ui/CustomSelect';
import UIRadioGroup, { UIRadio } from 'components/custom-ui/UIRadioGroup';
import { CenterContentContainer, ContentHeaderView, FormContainer, FormRowView, PageContainer, PageContentView } from 'components/shared';
import { UITextInput } from 'components/ui';
import React from 'react';
import PayeeDetailsFormView from './components/PayeeDetailsFormView';
import { FormButtonRow } from 'components/shared/containers/FormContainer';
import { PrimaryButton, SecondaryButton, UICheckbox } from 'components/ui';
import "./mobile.css";
import "./styles.css";

/**
 * @function PayeeDetailsPage
 * @return {React.Component}
 * @exported true
 * @description The page that shows the Payee Details
 */

export default function PayeeDetailsPage(props){
  const navigate = useRoute(); 
  const globalController = useBCMSContext();
  BCMSRecord.globalControllerFor(Classes, globalController);
  const data = { ...getDefaultData(), ...props.data ?? {} };
  const ui = { ...getDefaultUI(), ...props.ui ?? {} };
  const { prevStep } = useWorkflow() ?? {};

  // An payee id was passed, so get the payee from the model to edit it.
  if(data.selectedPayeeID) {
    data.isEditing = true;
    data.payee = Payee.initByID("0");
  } else {
    data.isEditingAccountNumber = true;
  }

  const controller = (state, setState) => ({
      data: state.data,
      ui: state.ui,

      onChange: prop => value => {
        const crud = BCMSStateManagerCRUD.init(state);

        // "Payee type" will also toggle the address type.
        if(prop === "payeeType") {
          // "onChange" is sometimes triggered when you open a dropdown, 
          // which can reset other values you have set. 
          // This "if" statements prevents that issue.
          if(data.payee.payeeType !== value) {
            crud.update([ "data", "payee", "addressType" ], value === "international" ? "Foreign" : "Domestic");
          }
        }
        
        crud.update([ "data", "payee", prop ], value);
        crud.submit(setState);
      },

      onChangeForm: prop => value => {
        BCMSStateManagerCRUD.init(state)
              .update([ "data", prop ], value)
              .submit(setState);
      },

      cancel: () => prevStep(-1)
  });


  return(
    <PageContainer className="payee-details-page" state={ { data, ui } } controller={ controller }>
        <PageContent/>
    </PageContainer>
  );
}

export const getDefaultData = () => ({
  isEditing: false,
  isEditingAccountNumber: false,
  selectedPayeeID: undefined,
  payee: Payee.init({
      "payeeType": "domestic",
      "addressType": "Domestic",
      "financialInstitutionNumberType": "aba",
      "receivingInstitutionNumberType": "aba",
      "intermediaryInstitutionNumberType": "aba",
      "requiresReceivingFinancialInstitutionDetails": "false",
      "requiresIntermediaryFinancialInstitutionDetails": "false"
  })
});

export const getDefaultUI = () => ({
  // no ui changes for now
});

function PageContent(props) {
  const { data, cancel, onChange, onChangeForm } = useStateManager();
  const payee = data.payee ?? {};

  return (
    <PageContentView>
      <CenterContentContainer>
        <ContentHeaderView text="Payee Details" />

        <FormContainer className="payee-details-form small-form-width">
          <FormRowView text="*Payee Type">
              <CustomSelect value={ data.payee.payeeType }  onChange={ onChange("payeeType") }>
                <CustomSelectItem value="domestic">Domestic</CustomSelectItem>
                <CustomSelectItem value="international">International</CustomSelectItem>
              </CustomSelect>
          </FormRowView>
          <b className="form-heading">Payee Information</b>
          <FormRowView text="* Nickname">
            <UITextInput value={ payee.nickname } onChange={ onChange("nickname") } />
          </FormRowView>
          <FormRowView text="* Payee Name">
            <UITextInput value={ payee.name } onChange={ onChange("name") } />
          </FormRowView>
          { data.isEditing &&
          <FormRowView>
            <UICheckbox onChange={ onChangeForm("isEditingAccountNumber") } label="I need to change the account number" />
          </FormRowView>
          }
          <FormRowView text="* Account Number">
            { !data.isEditingAccountNumber && <b>{ payee.account }</b> }
            { data.isEditingAccountNumber && 
            <UITextInput value={ payee.account } onChange={ onChange("account") } /> 
            }
          </FormRowView>
          { data.isEditingAccountNumber && 
            <FormRowView text="* Confirm Account Number">
              <UITextInput />
            </FormRowView>
          }
          <FormRowView text="* Address Type" className="address-type">
            <div className="ui-radio-container flex-container flex-horizontal align-center">
            <UIRadioGroup value={ data.payee.addressType } onChange={ onChange("addressType") }>
              <UIRadio label="Domestic" name="payee-address-type" className="domestic-ui-radio" />
              <UIRadio label="Foreign" name="payee-address-type" className="foreign-ui-radio"  />
            </UIRadioGroup>
            </div>
          </FormRowView>
            <PayeeDetailsFormView />
          <FormButtonRow>
            <SecondaryButton text="Cancel" onClick={ cancel } />
            <DisabledPrompt><PrimaryButton text="Save" /></DisabledPrompt>
          </FormButtonRow>
        </FormContainer>
        
      </CenterContentContainer>
    </PageContentView>
  );
}